import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { get, noop } from 'lodash'
import {
  useLazyGetAllPaymentMethodsQuery,
  useLazyGetIsSurchargeAppliedQuery,
  useStartPendingPaymentMutation,
} from 'api/payment-gateway/paymentGateway'
import HHFormSelectPaymentAmountField from 'components/payments/HHFormSelectPaymentAmountField'
import { Button, Grid, ListItem, ListItemText, Typography, Box } from '@mui/material'
import { ACH_ACCOUNT_TYPE, AMOUNT_TYPES, PAYMENT_METHODS } from 'settings/constants/payment'
import HHFormPriceField from 'components/form-fields/v5/HHFormPriceField'
import T from 'T'
import TransactionSurchargeAlert from 'containers/payments/TransactionSurchargeAlert'
import SurchargePanel from 'components/payments/SurchargePanel'
import RenderPaymentForm from 'components/payments/RenderPaymentForm'
import CVVConfirmation from 'containers/payments/CVVConfirmation'
import SelectPaymentMethodListItem from 'components/payments/PaymentActions/SelectPaymentMethodListItem'
import useMakePayment from 'components/payments/PaymentActions/useMakePayment'
import LoadingButton from '@mui/lab/LoadingButton'
import PaymentStatusDialog from 'components/payments/PaymentActions/PaymentStatusDialog'
import MakePaymentSkeleton from 'components/payments/PaymentActions/MakePaymentSkeleton'
import api from 'api'
import { CACHE_TAG_ACCOUNT_PAYMENT_TRANSACTION_LIST } from 'api/cacheTagTypes'
import { MAKE_PAYMENT_MODEL } from 'components/payments/settings/methodModel'
import usePaymentProvider from 'hooks/usePaymentProvider'
import FortisElements from 'components/fortis/FortisElements'
import { toast } from 'react-toastify'
import { HHAlert } from 'components/common/HHAlert'
import { useLazyGetBillingAddressQuery } from 'api/billing/billingAddress'

const { OTHER_AMOUNT } = AMOUNT_TYPES

// Fortis' documented constraint for the transaction_id path param on GET /transactions/{id} —
// the exact lookup the backend performs. A 'done' event whose data.id doesn't match this is not a
// real transaction id (e.g. an error-response id), so posting it would create a payment the backend
// can never reconcile.
const FORTIS_TRANSACTION_ID_PATTERN = /^(([0-9a-fA-F-]{24,36})|([0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}))$/

const MakePayment = () => {
  const businessId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.businessId'), shallowEqual)
  const accountId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.accountId'), shallowEqual)
  const { isFortis } = usePaymentProvider()

  const [getAllPaymentMethods, { isLoading: isLoadingPaymentMethods, data: paymentMethodsData }] = useLazyGetAllPaymentMethodsQuery()
  const [getBillingAddress, { data: billingAddressData }] = useLazyGetBillingAddressQuery()

  const paymentMethods = useMemo(() => get(paymentMethodsData, 'paymentMethods', []), [paymentMethodsData])
  const hasPaymentMethods = useMemo(() => paymentMethods.length > 0, [paymentMethods])
  const [getSurcharge, { data: surchargeData }] = useLazyGetIsSurchargeAppliedQuery()
  const surchargeApplied = useMemo(() => Boolean(surchargeData), [surchargeData])
  const billingAddress = useMemo(() => get(billingAddressData, 'billingAddress', {}), [billingAddressData])

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const {
    makePayment,
    isMakingPayment,
    hasPaymentError,
    isChargedByFortis,
    isPaymentSuccessful,
    transaction,
    unpaidInvoices,
    formValues,
    setValue,
    control,
    setValues,
    currentAmountDue,
    amountCentsToPay,
    getAmountToPayWithInvoices,
  } = useMakePayment()
  const [startPendingPayment] = useStartPendingPaymentMutation()
  const dispatch = useDispatch()
  const { amountDueOption, selectedPaymentMethod, showOtherPayment, type, selectedPaymentMethodId, cvv, saveMethod } = formValues
  const [paymentStatusDialog, setPaymentStatusDialog] = useState(false)
  const isOtherPaymentEnabled = useMemo(() => showOtherPayment === true, [showOtherPayment])
  const isOtherAmountOptionSelected = useMemo(() => amountDueOption === OTHER_AMOUNT, [amountDueOption])
  const isCardSavedMethod = useMemo(
    () =>
      Boolean(selectedPaymentMethod) &&
      !showOtherPayment &&
      selectedPaymentMethod.accountType !== 'ECHK' &&
      selectedPaymentMethod.accountType !== 'Checking',
    [selectedPaymentMethod, showOtherPayment]
  )
  const isCardNewMethod = useMemo(() => isOtherPaymentEnabled && type === PAYMENT_METHODS.CREDIT_CARD, [isOtherPaymentEnabled, type])
  const isFortisAch = useMemo(
    () =>
      (selectedPaymentMethod && selectedPaymentMethod.accountType?.toLowerCase() === 'checking') ||
      type === PAYMENT_METHODS.ACH ||
      (transaction && transaction.paymentMethod === 'ACH'),
    [selectedPaymentMethod, type, transaction]
  )
  const shouldShowSurchargeAlert = useMemo(
    () => surchargeApplied && (isCardNewMethod || isCardSavedMethod),
    [surchargeApplied, isCardNewMethod, isCardSavedMethod]
  )

  const toggleOtherPayment = () => {
    const next = !showOtherPayment
    setValue('showOtherPayment', next)
    if (next) {
      // Deselect saved method when opening Other Payment Method
      setValue('selectedPaymentMethodId', '')
      setValue('selectedPaymentMethod', '')
    } else {
      // Clear other payment form fields when closing
      setValue('accountToken', '')
      setValue('expiry', '')
      setValue('saveMethod', false)
      setValue('transactionRef', '')
      setValue('accountType', ACH_ACCOUNT_TYPE[0])
      setValue('cardHolderName', '')
      setValue('cardNickName', '')
      setValue('type', PAYMENT_METHODS.CREDIT_CARD)
      setValue('cvv', '')

      // Re-select the first saved payment method if available
      if (hasPaymentMethods) {
        setValue('selectedPaymentMethodId', paymentMethods[0].id)
        setValue('selectedPaymentMethod', paymentMethods[0])
      }
    }
  }

  const onCVVChange = value => setValue('cvv', value)
  const closeCVVModal = () => {
    setConfirmationModalOpen(false)
  }

  const openPaymentStatusDialog = () => {
    setPaymentStatusDialog(true)
  }

  const closePaymentStatusDialog = () => {
    setPaymentStatusDialog(false)
    // Reset form state after a successful payment so the next interaction starts fresh
    if (isPaymentSuccessful) {
      setValues({ ...MAKE_PAYMENT_MODEL })
    }
    setTimeout(() => {
      dispatch(api.util.invalidateTags([CACHE_TAG_ACCOUNT_PAYMENT_TRANSACTION_LIST]))
    }, 5000)
  }

  useEffect(() => {
    if (isPaymentSuccessful) {
      openPaymentStatusDialog()
    }
  }, [isPaymentSuccessful])

  const handleMakePaymentClick = () => {
    makePayment()
  }

  useEffect(() => {
    if (businessId && accountId) {
      getAllPaymentMethods({ businessId, accountId })
      getSurcharge({ businessId })
      getBillingAddress({ businessId, accountId })
    }
  }, [businessId, accountId])

  useEffect(() => {
    if (hasPaymentMethods) {
      setValue('selectedPaymentMethodId', paymentMethods[0].id)
      setValue('selectedPaymentMethod', paymentMethods[0])
    }
  }, [hasPaymentMethods, paymentMethods])

  useEffect(() => {
    if (isMakingPayment) {
      openPaymentStatusDialog()
    }
  }, [isMakingPayment])

  const gridItemSize = { xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }

  const isLoading = isLoadingPaymentMethods && !paymentMethodsData
  if (isLoading) {
    return <MakePaymentSkeleton />
  }

  const handleChangeSaveMethod = newSaveMethod => {
    setValue('saveMethod', newSaveMethod)
  }

  // Written before Fortis can charge, so a sale whose record call never lands is still booked. Nothing is charged
  // when this write fails.
  const handleBeforeNewCardPayment = paymentId => {
    const { amountCents } = getAmountToPayWithInvoices()
    return startPendingPayment({ businessId, payload: { accountId, aphId: paymentId, amountCents } })
      .unwrap()
      .then(() => true)
      .catch(() => {
        toast.error(T.PAYMENT_NOT_COMPLETED)
        return false
      })
  }

  const handleNewCardPaymentWithFortis = (event, paymentId) => {
    const eventType = event && (event['@type'] || event.type)
    if (eventType === 'done') {
      // Fortis fires 'done' even when the underlying request errored, in which case data.id is an
      // error-response id rather than a transaction id. Posting that leaves the payment unreconcilable
      // (the backend's transaction lookup fails). Only proceed when data.id is a valid transaction id.
      const eventData = get(event, 'data')
      const transactionId = get(eventData, 'id')
      if (!FORTIS_TRANSACTION_ID_PATTERN.test(transactionId || '')) {
        // On error Fortis returns an envelope ({ statusCode, title, detail, ... }) in place of a
        // transaction. Surface its detail/title when present; otherwise fall back to a generic message.
        const reason = get(eventData, 'detail') || get(eventData, 'title')
        toast.error(
          typeof reason === 'string' && reason.trim()
            ? `Payment could not be completed: ${reason.trim()}`
            : 'Payment could not be completed. Please try again.'
        )
        return
      }
      makePayment(event, paymentId)
      // toast.success('Payment approved')
      setTimeout(() => {
        dispatch(api.util.invalidateTags([CACHE_TAG_ACCOUNT_PAYMENT_TRANSACTION_LIST]))
      }, 1000)
    } else if (eventType === 'error') {
      const message = event && (event.message || (event.error && event.error.message))
      toast.error(message ? `Payment failed: ${message}` : 'Payment failed')
    }
  }

  return (
    <>
      <Grid container>
        <Grid item container xs={12} columnSpacing={2} rowSpacing={0}>
          <Grid item {...gridItemSize}>
            <ListItem disableGutters>
              <ListItemText
                disableTypography
                primary={
                  <Typography variant="body2" fontWeight={600}>
                    {T.PAYMENT_AMOUNT}
                  </Typography>
                }
                secondary={
                  <HHFormSelectPaymentAmountField
                    fullWidth
                    name="amountDueOption"
                    control={control}
                    options={unpaidInvoices}
                    currentAmountDue={currentAmountDue}
                    getOptionValue={option => option.invoiceNumber}
                  />
                }
              />
            </ListItem>
          </Grid>
          <Grid item {...gridItemSize}>
            {isOtherAmountOptionSelected && (
              <ListItem disableGutters>
                <ListItemText
                  disableTypography
                  primary={
                    <Typography variant="body2" fontWeight={600}>
                      {T.OTHER_AMOUNT}
                    </Typography>
                  }
                  secondary={<HHFormPriceField fullWidth control={control} name="otherAmount" />}
                />
              </ListItem>
            )}
          </Grid>
        </Grid>
        <Grid item container rowSpacing={2} columnSpacing={2} xs={12}>
          {paymentMethods.map(method => {
            const onPaymentMethodChange = () => {
              setValue('selectedPaymentMethodId', method.id)
              setValue('selectedPaymentMethod', method)
              setValue('showOtherPayment', false)
            }

            return (
              <Grid key={method.id} item {...gridItemSize}>
                <SelectPaymentMethodListItem
                  method={method}
                  selectedPaymentMethodId={selectedPaymentMethodId}
                  onChange={onPaymentMethodChange}
                />
              </Grid>
            )
          })}
          <React.Fragment key="other-payment-section">
            <Grid item container xs={12} rowSpacing={2} columnSpacing={2}>
              <Grid item {...gridItemSize}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={toggleOtherPayment}
                  sx={{
                    '&:focus-visible': theme => ({
                      outline: `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: 2,
                    }),
                  }}
                >
                  {T.OTHER_PAYMENT_METHOD}
                </Button>
              </Grid>

              {isOtherPaymentEnabled && (
                <>
                  <Grid item {...gridItemSize} />
                  {isFortis ? (
                    <Grid item {...gridItemSize}>
                      {amountCentsToPay <= 0 ? (
                        <HHAlert sx={{ mt: 2 }} severity="warning">
                          Please select an amount greater than 0 to continue.
                        </HHAlert>
                      ) : (
                        <FortisElements
                          saveMethod={saveMethod}
                          onChangeSaveMethod={handleChangeSaveMethod}
                          amount={amountCentsToPay}
                          onBeforeSubmit={handleBeforeNewCardPayment}
                          onPaymentFinished={handleNewCardPaymentWithFortis}
                          surchargeApplied={surchargeApplied}
                          billingAddress={billingAddress}
                        />
                      )}
                    </Grid>
                  ) : (
                    <RenderPaymentForm
                      isLoading={false}
                      onChange={setValue}
                      onChangeBulk={setValues}
                      formFields={formValues}
                      onClose={noop}
                      onSave={noop}
                      showSaveButton={false}
                      showSaveCheckbox
                    />
                  )}
                </>
              )}
              <Grid item {...gridItemSize}>
                <Box sx={{ width: '100%' }} />
              </Grid>
            </Grid>
          </React.Fragment>
        </Grid>

        <Grid item {...gridItemSize}>
          <Box sx={{ width: '100%' }} />
        </Grid>
        <Grid item container columnSpacing={2} xs={12}>
          {shouldShowSurchargeAlert && (
            <Grid item {...gridItemSize}>
              {/* Rate estimates come from the Fortis calculate-surcharge API, so the rate panel is
                  Fortis-only; other gateways keep the generic rate-less disclosure. */}
              {isCardSavedMethod && isFortis ? (
                <SurchargePanel
                  businessId={businessId}
                  amountCents={amountCentsToPay}
                  zip={get(billingAddress, 'zipCode', '')}
                  token={get(selectedPaymentMethod, 'accountToken', '')}
                />
              ) : (
                <TransactionSurchargeAlert />
              )}
            </Grid>
          )}
          <Grid item {...gridItemSize}>
            <Box sx={{ width: '100%' }} />
          </Grid>
        </Grid>
        <Grid item {...gridItemSize}>
          {(!showOtherPayment || !isFortis) && (
            <LoadingButton
              sx={{ mt: 2 }}
              loading={isMakingPayment}
              fullWidth
              variant="contained"
              onClick={handleMakePaymentClick}
              disabled={
                amountCentsToPay <= 0 || (showOtherPayment && !formValues.accountToken) || (!showOtherPayment && !selectedPaymentMethodId)
              }
            >
              {T.MAKE_PAYMENT_BUTTON}
            </LoadingButton>
          )}
        </Grid>
      </Grid>
      <PaymentStatusDialog
        isOpen={paymentStatusDialog}
        onClose={closePaymentStatusDialog}
        isMakingPayment={isMakingPayment}
        transaction={transaction}
        isACH={isFortisAch}
        hasPaymentError={hasPaymentError}
        isChargedByFortis={isChargedByFortis}
        isPaymentSuccessful={isPaymentSuccessful}
        businessId={businessId}
        accountId={accountId}
      />
      <CVVConfirmation
        cvv={cvv}
        confirmationModalOpen={confirmationModalOpen}
        closeCVVModal={closeCVVModal}
        onCVVChange={onCVVChange}
        handleMakePayment={makePayment}
        isProceedLoading={false}
      />
    </>
  )
}

MakePayment.propTypes = {}

export default MakePayment
